import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import Tippy from '@tippyjs/react';

import { isBrowser } from '@tager/web-core';

import KeyboardArrowUpIcon from '@/assets/svg/keyboard_arrow_up.svg';
import KeyboardArrowDownIcon from '@/assets/svg/keyboard_arrow_down.svg';
import LockIcon from '@/assets/svg/lock.svg';
import { DisplayIcon } from '@/components/Collapse/Collapse';
import { colors } from '@/constants/theme';
import Tooltip from '@/components/Collapse/components/Tooltip';
import { ScrollStyles } from '@/components/styles';
import { media } from '@/utils/mixin';
import useCollapse from '@/hooks/useCollapse';

import { inputsTitle } from './CollapsedList.constants';

interface Props {
  label: string;
  children: React.ReactNode;
  isExpandedList: boolean;

  handleExpandedInner(state: boolean): void;

  disabled?: boolean;
  hintText?: string;
  isClickable?: boolean;
}

function HorizontalCollapsedList({
  children,
  label,
  isExpandedList,
  handleExpandedInner,
  disabled,
  hintText,
  isClickable,
}: Props) {
  const [isExpanded, setExpanded] = useState(false);
  const { getCollapseProps, getToggleProps } = useCollapse({ isExpanded });

  useEffect(() => {
    setExpanded(isExpandedList);
  }, [isExpandedList]);

  useEffect(() => {
    handleExpandedInner(isExpanded);
  }, [isExpanded, handleExpandedInner]);

  function handleOpen() {
    if (disabled) return;
    handleExpandedInner(!isExpanded);
  }

  return (
    <Component>
      <Display
        isClickable={isClickable}
        {...getToggleProps({
          onClick: handleOpen,
        })}
      >
        {inputsTitle.includes(label) ? (
          <Title>{label}</Title>
        ) : (
          <SelectedTitle>{label}</SelectedTitle>
        )}
        {disabled ? (
          <Tippy
            content={<Tooltip text={hintText} />}
            offset={[25, 10]}
            placement="bottom"
            interactive
            appendTo={isBrowser() ? document.body : 'parent'}
          >
            <DisplayIcon>
              <LockIcon />
            </DisplayIcon>
          </Tippy>
        ) : (
          <DisplayIcon>
            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </DisplayIcon>
        )}
      </Display>
      <Content {...getCollapseProps()}>
        <ContentContainer>{children}</ContentContainer>
      </Content>
    </Component>
  );
}

export default HorizontalCollapsedList;

const Component = styled.div`
  width: 35%;
  margin-right: 26px;
  position: relative;

  &:last-child {
    margin-right: 0;
  }

  ${media.mobile(
    css`
      width: 100%;
      margin: 0 0 20px;
    `
  )}
`;

const Content = styled.div`
  position: absolute;
  width: 100%;
  max-height: 200px;
  overflow-y: scroll !important;
  border-radius: 8px;
  box-shadow: ${(props) => props.theme.blackShadow100};
  background: ${(props) => props.theme.white_1};
  z-index: 500;

  ${ScrollStyles}
`;

const ContentContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  height: fit-content;
  padding: 10px 20px;
  text-align: left;
  justify-content: space-between;
`;

const Display = styled.div<{ isClickable?: boolean }>`
  position: relative;
  display: flex;
  text-align: left;
  padding: 18px 20px;
  border: 1px solid ${(props) => props.theme.gray1800};
  border-radius: 4px;
  cursor: pointer;
  height: 52px;
  background: ${(props) => props.theme.white_1};

  svg {
    fill: ${colors.main};

    path {
      fill: ${colors.main};
    }
  }

  ${({ isClickable }) =>
    isClickable &&
    css`
      cursor: default;
    `}

  ${({ isClickable }) =>
    isClickable &&
    css`
      pointer-events: none;
    `}
`;

const Title = styled.span`
  height: 18px;
  width: 100%;
  font-size: 15px;
  line-height: 18px;
  color: ${(props) => props.theme.black};
`;

const SelectedTitle = styled.span`
  height: 18px;
  width: 100%;
  font-size: 18px;
  line-height: 18px;
  font-weight: 500;
  text-align: center;
  color: ${(props) => props.theme.black};
`;
